import { useGraphCacheStore } from '@tunasong/graph-cache'
import { logger } from '@tunasong/models'
import type { Persisted, Entity } from '@tunasong/schemas'
import type { Middleware } from 'redux'
import invariant from 'tiny-invariant'
import { entitiesApi } from '../../api/entities.js'
import { entitiesSlice } from './entities-slice.js'

// The RTK Query `updateQueryData` thunk is typed against the entitiesApi's
// root state, which we cannot reference here without creating an import cycle
// with configure-store. The runtime dispatch accepts the thunk regardless, so
// we use a local shape that just allows dispatching it.
type ThunkishDispatch = <TAction>(action: TAction) => TAction

/**
 * Sync entities actions with the cache.
 */

/** Insert the entity into a cached list, or replace it if already present. */
const upsertIntoList = (draft: Persisted<Entity>[], entity: Persisted<Entity>) => {
  const idx = draft.findIndex(e => e.id === entity.id)
  if (idx >= 0) {
    draft[idx] = entity
  } else {
    draft.push(entity)
  }
}

/**
 * When an entity arrives via `receiveEntity` (e.g. from a server push), the
 * Zustand graph-cache is updated by this middleware, but RTK Query's list
 * caches have no mechanism to discover the new entity on their own.
 *
 * Patch whichever list the entity belongs to — its parent's children, or the
 * ROOT list when it has no parent — and invalidate the matching tag to refetch
 * any subscriber that hadn't populated a cache entry yet. The tag invalidation
 * covers cache-miss races; the patch avoids an extra round-trip when the
 * subscription is already warm.
 */
const syncEntityListCache = (dispatch: ThunkishDispatch, entity: Persisted<Entity>) => {
  const parentId = entity.parentId
  if (!parentId) {
    /**
     * A parentless entity belongs to the ROOT list, not to any children list.
     * This case used to return early, so a pushed root entity updated the
     * Zustand graph-cache and nothing else — leaving the dashboard Inbox, which
     * reads `loadAllEntities` via `useEntities({ root: true })`, stale until a
     * reload (GH #1758).
     */
    dispatch(
      entitiesApi.util.updateQueryData('loadAllEntities', {}, draft => {
        upsertIntoList(draft, entity)
      })
    )
    dispatch(entitiesApi.util.invalidateTags([{ type: 'RootEntities' }]))
    return
  }
  dispatch(
    entitiesApi.util.updateQueryData('loadChildEntities', { parentId }, draft => {
      upsertIntoList(draft, entity)
    })
  )
  dispatch(entitiesApi.util.invalidateTags([{ type: 'ChildEntities', id: parentId }]))
}

const middleware: Middleware = // Most middleware do not modify the dispatch return value
  api => next => action => {
    const { partialUpdateCache, upsertCache } = useGraphCacheStore.getState()

    if (entitiesSlice.actions.receiveEntity.match(action)) {
      logger.debug('Middleware: Received entity', action.payload.entity)

      // Trigger an update of the cache.
      upsertCache(action.payload.entity)
      syncEntityListCache(api.dispatch as unknown as ThunkishDispatch, action.payload.entity)
    } else if (entitiesSlice.actions.receivePartialEntity.match(action)) {
      logger.debug('Middleware: Received partial entity', action.payload)
      invariant(action.payload.id, 'Partial entity must have an id')

      partialUpdateCache(action.payload.id, action.payload)
    }
    /** Always apply the action to other reducer(s)  */
    next(action)
  }

export interface GraphCacheStats {
  /** Entities held in the graph-cache `entityCache`. */
  entries: number
  /** Total characters of the legacy inline base64 `yDoc` strings those entities carry. */
  yDocChars: number
  /** Total characters of the entities serialized as JSON: a size proxy for everything the cache holds. */
  jsonChars: number
}

/**
 * Snapshot of the graph-cache size. Exposed on `globalThis.__tunaGraphCacheStats` for the perf memory gate.
 * Serializes every entry, so it is for diagnostics only — never call it on a hot path.
 */
export const getGraphCacheStats = (): GraphCacheStats => {
  const { entityCache } = useGraphCacheStore.getState()
  let yDocChars = 0
  let jsonChars = 0
  for (const entity of entityCache.values()) {
    yDocChars += typeof entity.yDoc === 'string' ? entity.yDoc.length : 0
    jsonChars += JSON.stringify(entity).length
  }
  return { entries: entityCache.size, yDocChars, jsonChars }
}

// Read-only diagnostic for the Playwright distinct-songs memory test (GH #1640, #1643), same pattern as
// `__tunaEditorStats` (GH #1666). Safe to ship: the test runs against the production build.
// eslint-disable-next-line no-underscore-dangle
;(globalThis as unknown as { __tunaGraphCacheStats?: typeof getGraphCacheStats }).__tunaGraphCacheStats =
  getGraphCacheStats

export default middleware
